// Shared invoice confirmation logic — single source of truth.
// Called by InvoiceScanner, InvoiceReviewPage, DuplicateReviewPage, ExpenseInvoiceReviewPage.
// Each step is a named function so failures are traceable.

import { supabase } from './supabase';
import { suggestEC, COGS_CATEGORIES } from './feeDetection';
import { normalizeAlias, postProcessExtraction } from './invoiceApi';
import { normalizeInvNumber, vendorsFuzzyMatch, cleanInvNumber } from './invoiceUtils';
import { parsePackSize } from '../utils/packSize';
import { createVendor, resolveVendor, assignVendorAlias } from './vendors';
import { checkInvoiceForAnomalies, spawnAnomalyTask } from './invoiceMonitoring';
import { logInvoiceScan, logPriceUpdate, logActivity } from './activityLog';
import { logExtraction } from './extractionLog';
import { gu } from '../utils/conversions';

// --- Step functions ---

async function checkDuplicate({ orgId, invNumber, vendor, invDate, grandTotal, pendingSupersede, queueRef, queueIdxRef }) {
  if (!orgId || !invNumber) return null;
  const norm = normalizeInvNumber(invNumber);
  const { data: allOrgInvs } = await supabase.from('invoices').select('id,vendor,grand_total,file_url,invoice_number,invoice_date,status,doc_quality,doc_quality_confidence,doc_timestamp,created_at').eq('org_id', orgId).is('superseded_by', null);
  const supersedeId = pendingSupersede?.id;
  const numMatches = (allOrgInvs || []).filter(r => normalizeInvNumber(r.invoice_number) === norm && r.id !== supersedeId);
  const dupByVendor = numMatches.filter(r => vendorsFuzzyMatch(r.vendor, vendor));
  const dupByDateTotal = numMatches.filter(r => !vendorsFuzzyMatch(r.vendor, vendor) && r.invoice_date === invDate && Math.abs(Number(r.grand_total || 0) - grandTotal) < 0.01);
  let match = null;
  if (dupByVendor.length) match = dupByVendor[0];
  else if (dupByDateTotal.length) { match = dupByDateTotal[0]; match._vendorDiffers = true; }
  // Queue check (bulk scan)
  if (!match && queueRef?.current?.length) {
    const ci = queueIdxRef?.current ?? -1;
    const qDup = queueRef.current.find((q, qi) => qi !== ci && q.status === 'confirmed' && q.extracted && normalizeInvNumber(q.extracted.invoice_number) === norm && vendorsFuzzyMatch(q.extracted.vendor, vendor));
    if (qDup) match = { id: 'queue', grand_total: qDup.extracted.grand_total || 0, invoice_number: invNumber };
  }
  if (match?.status === 'confirmed') match._isConfirmed = true;
  return match;
}

async function uploadFile({ orgId, invoiceId, uploadedFile, storagePath, invoicePages }) {
  let fileUrl = null;
  if (uploadedFile) {
    const filePath = `${orgId}/${invoiceId}.${uploadedFile.ext}`;
    const fileBytes = Uint8Array.from(atob(uploadedFile.base64), c => c.charCodeAt(0));
    const { error } = await supabase.storage.from('invoices').upload(filePath, fileBytes, { upsert: false });
    if (!error) fileUrl = filePath; else console.error('[confirmInvoice] file upload error:', error);
  } else if (storagePath) {
    const ext = (storagePath.split('.').pop() || 'pdf').toLowerCase();
    const filePath = `${orgId}/${invoiceId}.${ext}`;
    const { data: dl, error: dlErr } = await supabase.storage.from('invoices').download(storagePath);
    if (!dlErr && dl) {
      const { error: upErr } = await supabase.storage.from('invoices').upload(filePath, dl, { upsert: false });
      if (!upErr) fileUrl = filePath; else console.error('[confirmInvoice] file re-upload error:', upErr);
    } else console.error('[confirmInvoice] file download error:', dlErr);
    // Delete temp file
    try { await supabase.storage.from('invoices').remove([storagePath]); } catch (e) {}
  } else if (invoicePages?.length > 0 && invoicePages[0].storagePath) {
    const tmpPath = invoicePages[0].storagePath;
    const ext = (tmpPath.split('.').pop() || 'pdf').toLowerCase();
    const filePath = `${orgId}/${invoiceId}.${ext}`;
    const { data: dl, error: dlErr } = await supabase.storage.from('invoices').download(tmpPath);
    if (!dlErr && dl) {
      const { error: upErr } = await supabase.storage.from('invoices').upload(filePath, dl, { upsert: false });
      if (!upErr) fileUrl = filePath; else console.error('[confirmInvoice] fallback re-upload error:', upErr);
    }
  }
  return fileUrl;
}

async function saveInvoiceRow({ invoiceId, uid, orgId, vendor, invNumber, invDate, grandTotal, fileUrl, extracted }) {
  const payload = {
    id: invoiceId, user_id: uid, org_id: orgId, vendor, invoice_number: invNumber, invoice_date: invDate,
    subtotal: extracted.subtotal, tax: extracted.tax || 0, freight: extracted.freight || 0,
    bottle_deposit: extracted.bottle_deposit || 0, grand_total: grandTotal, total: grandTotal,
    status: 'confirmed', uploaded_by_name: '', file_url: fileUrl,
    doc_type: extracted.doc_type || 'invoice',
    doc_quality: extracted.document_quality || null,
    doc_quality_confidence: typeof extracted.document_quality_confidence === 'number' ? extracted.document_quality_confidence : null,
    doc_timestamp: extracted.document_timestamp || null,
  };
  const result = await supabase.from('invoices').insert(payload);
  return result;
}

async function executePendingSupersede({ pendingSupersede, invoiceId, orgId, vendor, invNumber, setPendingSupersede }) {
  if (!pendingSupersede) return;
  const ps = pendingSupersede;
  await supabase.from('invoices').update({ superseded_by: invoiceId }).eq('id', ps.id);
  await supabase.from('purchases').update({ is_superseded: true }).eq('invoice_id', ps.id);
  await supabase.from('categorized_expenses').update({ is_superseded: true }).eq('source_invoice_id', ps.id);
  await supabase.from('tasks').insert({ org_id: orgId, type: 'auto_resolved_duplicate', status: 'completed', completed_at: new Date().toISOString(), title: `Auto-resolved: ${vendor} #${invNumber}`, note: ps.reason, source: 'system', priority: 3, target_page: 'swappedinvoices', payload: { superseded_invoice_id: ps.id, new_invoice_id: invoiceId, kept: 'new_upload', reason: ps.reason, total_diff: ps.totalDiff } });
  if (setPendingSupersede) setPendingSupersede(null);
}

async function autoSupersedeConfirmations({ orgId, invNumber, invoiceId, docType }) {
  if (docType !== 'invoice' || !invNumber) return;
  const { data: superseded } = await supabase.from('invoices').select('id').eq('org_id', orgId).eq('invoice_number', invNumber).eq('doc_type', 'confirmation').is('superseded_by', null);
  if (!superseded?.length) return;
  const confIds = superseded.map(r => r.id);
  await supabase.from('invoices').update({ superseded_by: invoiceId }).in('id', confIds);
  for (const cid of confIds) {
    await supabase.from('purchases').update({ is_superseded: true }).eq('invoice_id', cid);
    await supabase.from('categorized_expenses').update({ is_superseded: true }).eq('source_invoice_id', cid);
  }
}

async function insertLineItems({ matched, invoiceId, uid, orgId }) {
  const rows = (matched || []).filter(i => i.amount || i.amount === 0).map(i => ({
    user_id: uid, org_id: orgId, invoice_id: invoiceId,
    item_number: i.item_number || null, description: i.description || null,
    pack_size: i.pack_size || null, brand: i.brand || null,
    qty_shipped: i.qty_shipped, unit: i.unit || null,
    unit_price: i.unit_price, amount: i.amount,
    catch_weight: i.catch_weight ?? null, line_tax: i.line_tax ?? null,
    category: i.category || null, matched_ingredient_id: i.matched_ingredient_id || null,
    price_changed: !!i.price_changed, old_price: i.old_price ?? null, new_price: i.new_price ?? null,
  }));
  if (rows.length) await supabase.from('invoice_line_items').insert(rows);
}

async function runAnomalyCheck({ orgId, invoiceId, vendor, invNumber, invDate, grandTotal, matched }) {
  try {
    const anomalies = await checkInvoiceForAnomalies(supabase, orgId, { id: invoiceId, vendor, invoice_number: invNumber, invoice_date: invDate, grand_total: grandTotal }, matched);
    if (anomalies?.length) await spawnAnomalyTask(supabase, orgId, invoiceId, anomalies);
  } catch (e) { console.error('[confirmInvoice] anomaly check error:', e); }
}

async function applyPriceUpdates({ matched, priceUpdates, ings, setIngs, invDate, uid, orgId }) {
  const approved = (matched || []).filter(i => i.price_changed && priceUpdates?.[i.matched_ingredient_id]);
  for (const item of approved) {
    const ig = (ings || []).find(x => x.id === item.matched_ingredient_id);
    if (!ig) continue;
    const isNewer = !ig.lastPriceDate || invDate >= ig.lastPriceDate;
    if (!isNewer) continue;
    const isCW = item.isCatchWeight || item.catch_weight != null;
    const updates = {};
    if (isCW) {
      updates.case_price = item.catchWeightAmount || item.amount;
      updates.case_qty = item.catch_weight;
      updates.last_price_date = invDate;
    } else {
      updates.case_price = ig?.cp ?? item.new_price;
      updates.last_price_date = invDate;
    }
    await supabase.from('ingredients').update(updates).eq('id', ig.id);
    if (setIngs) setIngs(p => p.map(x => x.id === ig.id ? { ...x, cp: updates.case_price, cq: updates.case_qty ?? x.cq, lastPriceDate: invDate, prevCp: x.cp } : x));
    const histPrice = item.new_price;
    await supabase.from('price_history').insert({ user_id: uid, org_id: orgId, ingredient_id: ig.id, old_price: item.old_price, new_price: histPrice, source: 'invoice', invoice_date: invDate });
  }
  return approved;
}

async function classifyAndCreateIngredients({ matched, extracted, vendor, invDate, invNumber, orgId, uid, ings, setIngs, ingsRef, newItemEC, newItemCat, priceUpdates, reconcileInfo, save }) {
  const cogsByCategory = {};
  const expenseByCategory = {};
  let expenseTotal = 0;
  const autoCreatedIngs = [];
  const skippedSet = new Set(reconcileInfo?.skippedLines || []);
  const createNewItems = reconcileInfo?.approvedNewItems || false;

  // Load ingredient categories for AI-assigned category validation
  let ingCats = null;
  try {
    const { data: catRows } = await supabase.from('ingredient_categories').select('name,id').eq('org_id', orgId);
    if (catRows?.length) ingCats = new Map(catRows.map(r => [r.name, r.id]));
  } catch (e) {}

  for (let i = 0; i < (matched || []).length; i++) {
    const item = matched[i];
    if (!item.amount && item.amount !== 0) continue;
    if (item.matched_ingredient_id) {
      const ig = (ings || []).find(x => x.id === item.matched_ingredient_id);
      const ec = ig?.ec || (ig?.cat === 'Beverage' ? 'Beverage' : 'Food');
      if (COGS_CATEGORIES.has(ec)) {
        cogsByCategory[ec] = (cogsByCategory[ec] || 0) + item.amount;
      } else {
        expenseTotal += item.amount;
        if (!expenseByCategory[ec]) expenseByCategory[ec] = [];
        expenseByCategory[ec].push({ desc: item.description, amt: item.amount, itemNumber: item.item_number || null, lineIndex: i });
      }
      // Apply reviewer-assigned category to matched ingredients missing one
      if (ig && !ig.categoryId && newItemCat?.[i] && ingCats) {
        const catId = ingCats.get(newItemCat[i]);
        if (catId) {
          await supabase.from('ingredients').update({ category: newItemCat[i], category_id: catId, category_source: 'manual', category_confidence: null }).eq('id', ig.id);
          if (setIngs) setIngs(p => p.map(x => x.id === ig.id ? { ...x, cat: newItemCat[i], categoryId: catId, categorySource: 'manual', categoryConfidence: null } : x));
        }
      }
    } else {
      const ec = newItemEC?.[i] || suggestEC(item.description, item.category);
      if (COGS_CATEGORIES.has(ec)) {
        cogsByCategory[ec] = (cogsByCategory[ec] || 0) + item.amount;
      } else {
        expenseTotal += item.amount;
        if (!expenseByCategory[ec]) expenseByCategory[ec] = [];
        expenseByCategory[ec].push({ desc: item.description, amt: item.amount, itemNumber: item.item_number || null, lineIndex: i });
      }
      // Auto-create ingredient (only if user approved and not skipped)
      if (COGS_CATEGORIES.has(ec) && createNewItems && !skippedSet.has(i)) {
        const isCW = item.catch_weight != null;
        const pack = parsePackSize(item.pack_size, item.pack_qty);
        const newId = `i${Date.now()}_${i}`;
        let cp, cu, ppc, spp, cq;
        if (isCW) {
          cp = Math.round(item.amount * 100) / 100;
          cq = item.catch_weight;
          cu = item.unit && item.unit !== 'CS' ? item.unit : 'LB';
          ppc = 1; spp = cq;
        } else {
          const casePrice = (item.amount - (item.line_tax || 0)) / (item.qty_shipped || 1);
          cp = Math.round(casePrice * 100) / 100;
          ppc = pack?.ppc || 1;
          spp = pack?.spp || 0;
          cq = pack?.cq || (ppc && spp ? Math.round(ppc * spp * 100) / 100 : 0);
          cu = pack?.pu || (item.unit && item.unit !== 'CS' ? item.unit : 'EACH');
        }
        let srv = null, sru = null, autoPcu = null;
        if (!isCW && ppc > 1) {
          const COUNT_UNITS = new Set(['EACH', 'SLICE', 'SERVING', 'PIECE']);
          if (COUNT_UNITS.has(cu)) { srv = cq || ppc; sru = 'EACH'; autoPcu = 'EACH'; }
        }
        const reviewedCat = newItemCat?.[i] || null;
        const rawAiCat = item.ingredient_category || null;
        const aiCat = rawAiCat && ingCats?.has(rawAiCat) ? rawAiCat : null;
        const finalCat = reviewedCat && ingCats?.has(reviewedCat) ? reviewedCat : (aiCat || null);
        const newIng = {
          id: newId, n: (item.description || '').toUpperCase(), b: (item.brand || '').toUpperCase(),
          cat: finalCat, dist: vendor.toUpperCase(), ec, cp, cu, ppc, spp, cq, srv, sru, pcu: autoPcu,
          vin: (item.item_number || '').toUpperCase() || null,
          yp: 100, tier: 3, loc: 'Dry Storage', par: 1, so: (ings?.length || 0) + i,
          lastPriceDate: invDate,
          categorySource: finalCat ? 'manual' : null, categoryConfidence: finalCat ? 1 : null,
        };
        if (finalCat && ingCats) { const catId = ingCats.get(finalCat); if (catId) newIng.categoryId = catId; }
        autoCreatedIngs.push({ newIng, desc: item.description });
      }
    }
  }
  return { cogsByCategory, expenseByCategory, expenseTotal, autoCreatedIngs };
}

async function persistNewIngredients({ autoCreatedIngs, orgId, uid, ings, setIngs, ingsRef, vendor, invNumber, invoiceId, save }) {
  for (const { newIng, desc } of autoCreatedIngs) {
    const { error: ingErr } = await (save?.insertIngredient?.(orgId, uid, newIng)) || {};
    if (ingErr) console.error('[confirmInvoice] ingredient insert failed:', newIng.n, ingErr);
    if (setIngs) setIngs(p => [...p, newIng]);
    if (ingsRef) ingsRef.current = [...ingsRef.current, newIng];
    if (desc) {
      await supabase.from('ingredient_aliases').upsert({ org_id: orgId, ingredient_id: newIng.id, alias: desc, normalized_alias: normalizeAlias(desc), source: 'auto_create' }, { onConflict: 'org_id,normalized_alias' });
    }
    logActivity(orgId, 'ingredient', 'auto_create', `Auto-created ingredient "${newIng.n}" from invoice ${invNumber || invoiceId}`, { ingredientId: newIng.id, vendor, invoiceId, invoiceNumber: invNumber, description: desc, brand: newIng.b, vin: newIng.vin, casePrice: newIng.cp });
  }
}

async function writeExpenseRecords({ expenseByCategory, orgId, uid, vendor, invDate, invoiceId, setOtherExp }) {
  for (const [cat, items] of Object.entries(expenseByCategory)) {
    for (const fee of items) {
      const expId = `ce${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const amt = Math.round(fee.amt * 100) / 100;
      await supabase.from('categorized_expenses').insert({
        id: expId, user_id: uid, org_id: orgId, date: invDate,
        category: cat, vendor, description: fee.desc, amount: amt,
        source_invoice_id: invoiceId,
        invoice_item_number: fee.itemNumber || null, invoice_line_index: fee.lineIndex ?? null,
      });
      if (setOtherExp) setOtherExp(p => [...p, { id: expId, desc: fee.desc, amt, date: invDate, category: cat, sourceInvoiceId: invoiceId }]);
    }
  }
}

async function writePurchaseRecord({ cogsByCategory, expenseTotal, grandTotal, orgId, uid, vendor, invDate, invoiceId, onConfirm }) {
  const cogsTotal = Math.round((grandTotal - expenseTotal) * 100) / 100;
  const dominantCat = Object.entries(cogsByCategory).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Food';
  const purchaseEntry = { id: `pu${Date.now()}`, vendor, amt: cogsTotal, date: invDate, category: dominantCat, invoiceId };
  if (cogsTotal > 0 && orgId && uid) {
    const { error: puErr } = await supabase.from('purchases').insert({ id: purchaseEntry.id, user_id: uid, org_id: orgId, vendor: purchaseEntry.vendor, amount: purchaseEntry.amt, date: purchaseEntry.date, category: purchaseEntry.category, invoice_id: invoiceId });
    if (puErr) console.error('[confirmInvoice] purchase insert error:', puErr);
  }
  if (onConfirm) onConfirm({ ...purchaseEntry, amt: cogsTotal > 0 ? cogsTotal : 0 });
  return cogsTotal;
}

async function backfillPackStructure({ matched, ings, setIngs, orgId, invDate, invoiceId, vendor }) {
  for (const item of (matched || [])) {
    if (!item.matched_ingredient_id || !item.pack_size) continue;
    const ig = (ings || []).find(x => x.id === item.matched_ingredient_id);
    if (!ig) continue;
    // Only backfill when pack is genuinely empty — never overwrite user-set values
    const isDefault = (ig.ppc || 0) <= 1 && (ig.spp === null || ig.spp === undefined || ig.spp === 0) && (ig.cq === null || ig.cq === undefined || ig.cq <= 1);
    if (!isDefault) continue;
    const pack = parsePackSize(item.pack_size, item.pack_qty);
    if (!pack || pack.ppc <= 1) continue;
    const updates = { packs_per_case: pack.ppc, size_per_pack: pack.spp || 0, case_qty: pack.cq || pack.ppc };
    if (pack.pu) updates.unit = pack.pu;
    await supabase.from('ingredients').update(updates).eq('id', ig.id);
    if (setIngs) setIngs(p => p.map(x => x.id === ig.id ? { ...x, ppc: pack.ppc, spp: pack.spp || 0, cq: pack.cq || pack.ppc, cu: pack.pu || x.cu } : x));
    logActivity(orgId, 'ingredient', 'pack_backfill', `Backfilled pack structure for "${ig.n}" from invoice ${invoiceId}`, { ingredientId: ig.id, oldPpc: ig.ppc, oldSpp: ig.spp, newPpc: pack.ppc, newSpp: pack.spp, source: vendor });
  }
}

async function saveVendorAndAliases({ orgId, vendor, extracted, vendorCategory }) {
  try { await createVendor(orgId, { name: vendor, vendor_category: vendorCategory }); } catch (e) { if (!e.message?.includes('already exists')) console.error('[confirmInvoice] auto-add vendor error:', e); }
  const resolved = await resolveVendor(orgId, vendor);
  if (resolved) {
    const rawVendor = (extracted.raw_vendor || extracted.vendor || '').trim();
    if (rawVendor) try { await assignVendorAlias(orgId, resolved.id, rawVendor); } catch (e) {}
    if (vendor !== resolved.name) try { await assignVendorAlias(orgId, resolved.id, vendor); } catch (e) {}
  }
}

async function spawnCostCardAlerts({ matched, priceUpdates, ings, mi, batches, bc, orgId, settings }) {
  const fcTarget = settings?.fcTarget || 30;
  const approved = (matched || []).filter(i => i.price_changed && priceUpdates?.[i.matched_ingredient_id]);
  const changedIngIds = approved.map(i => i.matched_ingredient_id).filter(Boolean);
  if (!changedIngIds.length || !mi?.length) return;
  const updatedIngs = (ings || []).map(ig => { const c = approved.find(x => x.matched_ingredient_id === ig.id); return c ? { ...ig, cp: c.new_price } : ig; });
  for (const cc of mi) {
    if (!cc.r?.some(l => changedIngIds.includes(l.i))) continue;
    let oldTot = 0; for (const l of cc.r) { const ba = batches?.find(b => b.id === l.i); if (ba && bc?.[ba.id]) oldTot += bc[ba.id].pu * l.q; else { const ig = (ings || []).find(x => x.id === l.i); if (ig) { const uc = gu(ig, l.u); if (uc !== null) oldTot += uc * l.q; } } }
    const oldPct = cc.p > 0 ? ((oldTot / (cc.yield || 1)) / cc.p) * 100 : 0;
    let newTot = 0; for (const l of cc.r) { const ba = batches?.find(b => b.id === l.i); if (ba && bc?.[ba.id]) newTot += bc[ba.id].pu * l.q; else { const ig = updatedIngs.find(x => x.id === l.i); if (ig) { const uc = gu(ig, l.u); if (uc !== null) newTot += uc * l.q; } } }
    const newPct = cc.p > 0 ? ((newTot / (cc.yield || 1)) / cc.p) * 100 : 0;
    if (oldPct <= fcTarget && newPct > fcTarget) {
      const changedIng = approved.find(x => cc.r.some(l => l.i === x.matched_ingredient_id));
      supabase.from('tasks').insert({ org_id: orgId, type: 'cost_card_review', status: 'pending', title: `Cost alert: ${cc.n} now ${newPct.toFixed(1)}% food cost`, note: `Was ${oldPct.toFixed(1)}%, now ${newPct.toFixed(1)}% after price update`, source: 'system', priority: 7, target_page: 'costcards', target_id: cc.id, overdue_at: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(), payload: { cost_card_id: cc.id, cost_card_name: cc.n, old_pct: oldPct, new_pct: newPct, ingredient_name: changedIng?.matched_name || '', price_change_pct: changedIng?.pct_change || 0 } });
    }
  }
}

// --- Main entry point ---

export async function sharedConfirmInvoice(params) {
  const {
    orgId, uid, extracted, matched, vendor, invNumber, invDate, grandTotal,
    confirmPath, reconcileInfo, priceUpdates, newItemEC, newItemCat,
    uploadedFile, storagePath, invoicePages,
    ings, setIngs, ingsRef, setOtherExp, setPurchases,
    settings, mi, batches, bc, save,
    onConfirm, refreshBell,
    pendingSupersede, setPendingSupersede,
    queueRef, queueIdxRef,
    expenseMode, expenseCategory, vendorCategory,
    skipDupCheck, preInsertedInvoiceId,
  } = params;

  const invoiceId = preInsertedInvoiceId || `inv_${Date.now()}`;
  const docType = extracted.doc_type || 'invoice';

  // Step 1: Duplicate check
  if (!skipDupCheck) {
    const dup = await checkDuplicate({ orgId, invNumber, vendor, invDate, grandTotal, pendingSupersede, queueRef, queueIdxRef });
    if (dup) return { blocked: 'duplicate', duplicate: dup };
  }

  if (!preInsertedInvoiceId) {
    // Step 2: Upload file
    const fileUrl = await uploadFile({ orgId, invoiceId, uploadedFile, storagePath, invoicePages });

    // Step 3: Insert invoice row
    const invResult = await saveInvoiceRow({ invoiceId, uid, orgId, vendor, invNumber, invDate, grandTotal, fileUrl, extracted });
    if (invResult.error) {
      if (invResult.error.message?.includes('idx_invoices_dedup') || invResult.error.code === '23505') {
        const { data: conflicting } = await supabase.from('invoices').select('id,vendor,grand_total,invoice_number').eq('org_id', orgId).eq('invoice_number', invNumber).is('superseded_by', null).limit(1);
        if (conflicting?.length) return { blocked: 'duplicate', duplicate: conflicting[0] };
      }
      return { error: 'Failed to save invoice: ' + invResult.error.message };
    }
  }

  // Step 4: Execute pending supersede
  await executePendingSupersede({ pendingSupersede, invoiceId, orgId, vendor, invNumber, setPendingSupersede });

  // Step 5: Auto-supersede confirmations
  await autoSupersedeConfirmations({ orgId, invNumber, invoiceId, docType });

  // Step 6: Insert line items
  await insertLineItems({ matched, invoiceId, uid, orgId });

  // Step 7: Anomaly monitoring
  await runAnomalyCheck({ orgId, invoiceId, vendor, invNumber, invDate, grandTotal, matched });

  // Step 8: Price updates
  const approvedChanges = await applyPriceUpdates({ matched, priceUpdates, ings, setIngs, invDate, uid, orgId });

  if (expenseMode) {
    // Expense path: single expense row for entire invoice
    const expId = `ce${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    await supabase.from('categorized_expenses').insert({
      id: expId, user_id: uid, org_id: orgId, date: invDate,
      category: expenseCategory || 'Miscellaneous', vendor,
      description: extracted.invoice_number ? `Invoice #${extracted.invoice_number}` : 'Invoice expense',
      amount: grandTotal, source_invoice_id: invoiceId,
      scanned_snapshot: { amount: grandTotal },
    });
    if (setOtherExp) setOtherExp(p => [...p, { id: expId, vendor, desc: extracted.invoice_number ? `Invoice #${extracted.invoice_number}` : 'Invoice expense', amt: grandTotal, date: invDate, category: expenseCategory || 'Miscellaneous', sourceInvoiceId: invoiceId }]);
  } else {
    // Step 9: Classify lines + create ingredients
    const { cogsByCategory, expenseByCategory, expenseTotal, autoCreatedIngs } = await classifyAndCreateIngredients({ matched, extracted, vendor, invDate, invNumber, orgId, uid, ings, setIngs, ingsRef, newItemEC, newItemCat, priceUpdates, reconcileInfo, save });

    // Step 10: Persist new ingredients
    await persistNewIngredients({ autoCreatedIngs, orgId, uid, ings, setIngs, ingsRef, vendor, invNumber, invoiceId, save });

    // Step 11: Write expense records
    await writeExpenseRecords({ expenseByCategory, orgId, uid, vendor, invDate: invDate, invoiceId, setOtherExp });

    // Step 12: Write purchase record
    await writePurchaseRecord({ cogsByCategory, expenseTotal, grandTotal, orgId, uid, vendor, invDate, invoiceId, onConfirm });

    // Step 13: Pack backfill
    await backfillPackStructure({ matched, ings, setIngs, orgId, invDate, invoiceId, vendor });
  }

  // Step 14: Vendor + aliases
  await saveVendorAndAliases({ orgId, vendor, extracted, vendorCategory: vendorCategory || 'inventory' });

  // Step 15: Activity logging
  logInvoiceScan(orgId, vendor, invNumber, extracted.grand_total || grandTotal, (matched || []).length, (approvedChanges || []).length);
  (approvedChanges || []).forEach(i => logPriceUpdate(orgId, i.matched_name, i.old_price, i.new_price));

  // Step 16: Extraction logging
  logExtraction(supabase, { orgId, invoiceId, confirmPath, extracted, matched, vendor, invNumber, invDate, grandTotal, lineEdits: reconcileInfo?.lineEdits, skippedLines: reconcileInfo?.skippedLines, extractionMs: extracted._extractionMs, docSource: extracted._docSource });

  // Step 17: Cost card alerts
  if (!expenseMode) {
    await spawnCostCardAlerts({ matched, priceUpdates, ings, mi, batches, bc, orgId, settings });
  }

  if (refreshBell) refreshBell();
  return { success: true, invoiceId };
}
